import { Button } from "@/components/ui/button";
import { IS_DEV } from "@/data/constants";
import { PosthogService } from "@/services/posthog";
import { POSTHOG_EVENT } from "@/services/posthog/data";
import {
  captureSentryException,
  captureSentryMessage,
} from "@/services/sentry";
import { InfoCircledIcon } from "@pkg/icons";
import { getAuth } from "firebase/auth";
import { isRouteErrorResponse, Link, useRouteError } from "react-router";
import errorImage from "./error.jpg";

function ErrorFallbackUI() {
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="bg-white">
      <div className="relative mx-auto flex h-dvh max-w-184 flex-col justify-center gap-5 bg-white px-4 py-10 text-center text-nightRider xl:px-0">
        <img src={errorImage} alt="" className="mx-auto w-full max-w-105" />

        <div>
          <p className="w-full font-erode-variable text-4xl leading-8 tracking-[-4] wrap-break-word md:text-[60px] md:leading-[120%]">
            Something went wrong!
          </p>

          <p className="mt-3 w-full text-center font-archivo leading-5 wrap-break-word text-suvaGrey md:text-2xl md:leading-[150%]">
            Sit tight, we're working on getting it fixed
          </p>
        </div>

        <div className="flex flex-col-reverse gap-2 sm:mx-auto sm:max-w-xl sm:flex-row sm:gap-4">
          <Button
            variant="dark"
            onClick={handleRefresh}
            className="w-full sm:w-auto"
          >
            Refresh Page
          </Button>

          <Button variant="outline" asChild className="w-full sm:w-auto">
            <Link to="/">Back Home</Link>
          </Button>
        </div>

        <p className="mt-14 items-center justify-center gap-1 text-center font-archivo text-xs leading-4 font-normal tracking-[0.48px] wrap-break-word text-suvaGrey md:mt-24 md:text-base md:leading-[27.2px]">
          <InfoCircledIcon className="mr-1 inline shrink-0 -translate-y-0.5" />
          Google Translate extension might be affecting this page. Try removing
          the extension.
        </p>
      </div>
    </div>
  );
}

/**
 * Route error element for React Router's errorElement prop.
 * Uses useRouteError to capture errors thrown during routing/rendering.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  // Report to Sentry
  if (error instanceof Error) {
    captureSentryException(error);
  } else if (isRouteErrorResponse(error)) {
    captureSentryMessage(`Route error: ${error.status} ${error.statusText}`, {
      level: "error",
      extra: { data: error.data },
    });
  }

  // Report to PostHog
  if (!IS_DEV) {
    try {
      const auth = getAuth();
      const userEmail = auth.currentUser?.email ?? undefined;

      PosthogService.capture(POSTHOG_EVENT.ROUTE_ERROR_BOUNDARY_TRIGGERED, {
        email: userEmail ?? "email-na",
        error_message:
          error instanceof Error
            ? error.message
            : isRouteErrorResponse(error)
              ? `${error.status} ${error.statusText}`
              : "Unknown error",
        error_type: isRouteErrorResponse(error) ? "route_error" : "exception",
        url: window.location.href,
      });
    } catch {
      // Silently fail -
    }
  }

  console.error("RouteErrorBoundary caught an error:", error);

  return <ErrorFallbackUI />;
}
